import React, { useState } from 'react';

import { queryCache, useMutation } from 'react-query';
import { Toast } from 'src/common/components';

import { patchFeatureCatalogEntry } from '../api';
import { CATEGORY_OPTIONS, FEATURE_CATALOG_QUERY_KEY, NO_CATEGORY_LABEL } from '../constants';
import { FeatureCatalogEntry, FeatureCatalogPatchPayload, FeatureCategory } from '../types';
import { getFeatureFieldErrors } from '../utils';

type FeatureEditFormProps = {
  entry: FeatureCatalogEntry;
  onCancel: () => void;
  onSaved: () => void;
};

const parseTags = (raw: string): string[] =>
  Array.from(
    new Set(
      raw
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
    )
  );

const LABEL_CLASSES = 'text-xs font-medium tracking-wide text-gray-500 uppercase';

const FieldError: React.FC<{ message?: string }> = ({ message }) =>
  message ? <span className="block mt-0.5 text-xs text-red-600">{message}</span> : null;

const FeatureEditForm: React.FC<FeatureEditFormProps> = ({ entry, onCancel, onSaved }) => {
  const [title, setTitle] = useState(entry.title);
  const [benefit, setBenefit] = useState(entry.customer_benefit);
  const [category, setCategory] = useState<FeatureCategory | null>(entry.category);
  const [guide, setGuide] = useState(entry.staff_setup_guide);
  const [articleUrl, setArticleUrl] = useState(entry.support_article_url);
  const [tags, setTags] = useState(entry.tags.join(', '));
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [save, { isLoading }] = useMutation(
    (payload: FeatureCatalogPatchPayload) => patchFeatureCatalogEntry(entry.id, payload),
    {
      onSuccess: () => {
        setErrors({});
        void queryCache.invalidateQueries(FEATURE_CATALOG_QUERY_KEY);
        Toast.success('Funcionalidad actualizada.');
        onSaved();
      },
      onError: (error) => {
        const fieldErrors = getFeatureFieldErrors(error);
        setErrors(fieldErrors);
        Toast.error(fieldErrors.detail || 'Revisa los campos marcados e intenta de nuevo.');
      },
    }
  );

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!title.trim()) {
      setErrors({ title: 'El título es obligatorio.' });
      return;
    }
    save({
      title: title.trim(),
      customer_benefit: benefit.trim(),
      category,
      staff_setup_guide: guide,
      support_article_url: articleUrl.trim(),
      tags: parseTags(tags),
    });
  };

  return (
    <form
      className="dg-card"
      style={{ borderRadius: 16, padding: '30px 32px 34px' }}
      onSubmit={handleSubmit}
    >
      <h2 className="mb-4 text-lg font-bold text-gray-900">Editar funcionalidad</h2>
      <div className="space-y-3">
        <label className="block">
          <span className={LABEL_CLASSES}>Título</span>
          <input
            className="input mt-0.5 w-full text-sm"
            value={title}
            maxLength={255}
            onChange={(e) => setTitle(e.target.value)}
          />
          <FieldError message={errors.title} />
        </label>

        <label className="block">
          <span className={LABEL_CLASSES}>Beneficio para el cliente</span>
          <textarea
            className="input mt-0.5 w-full text-sm"
            rows={3}
            value={benefit}
            onChange={(e) => setBenefit(e.target.value)}
          />
          <FieldError message={errors.customer_benefit} />
        </label>

        <label className="block">
          <span className={LABEL_CLASSES}>Área</span>
          <select
            className="input mt-0.5 w-full text-sm"
            value={category ?? ''}
            onChange={(e) => setCategory((e.target.value as FeatureCategory) || null)}
          >
            <option value="">{NO_CATEGORY_LABEL}</option>
            {CATEGORY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <FieldError message={errors.category} />
        </label>

        <label className="block">
          <span className={LABEL_CLASSES}>Guía de configuración (staff interno)</span>
          <textarea
            className="input mt-0.5 w-full text-sm"
            rows={4}
            value={guide}
            placeholder="Ej: Ir a Telescope → cuenta → Settings → Módulo X → activar..."
            onChange={(e) => setGuide(e.target.value)}
          />
          <FieldError message={errors.staff_setup_guide} />
        </label>

        <label className="block">
          <span className={LABEL_CLASSES}>Artículo de ayuda (URL)</span>
          <input
            className="input mt-0.5 w-full text-sm"
            type="url"
            value={articleUrl}
            placeholder="https://help.brainhi.com/..."
            onChange={(e) => setArticleUrl(e.target.value)}
          />
          <FieldError message={errors.support_article_url} />
        </label>

        <label className="block">
          <span className={LABEL_CLASSES}>Etiquetas (separadas por coma)</span>
          <input
            className="input mt-0.5 w-full text-sm"
            value={tags}
            placeholder="facturacion, mensajes"
            onChange={(e) => setTags(e.target.value)}
          />
          <FieldError message={errors.tags} />
        </label>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </form>
  );
};

export default FeatureEditForm;
